import styled from '@emotion/styled';
import { ReactComponent as CrossSimpleIconComponent } from 'assets/icons/cross-simple.svg';

/* -----------------------------------------------------------------------------
 * Assessment report — redesigned to mirror the eligibility-check modal look:
 * rounded cards, soft shadows, purple accent, gradient heroes, pill badges.
 * The old blue/formBackground primitives have been replaced; we re-export a
 * few names kept for backwards-compatibility (Badge, CrossSimpleIcon) so any
 * lingering references don't break.
 * ---------------------------------------------------------------------------*/

export const Page = styled.section`
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 24px 24px 40px;
    max-width: 1280px;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;

    @media screen and (max-width: 768px) {
        padding: 16px 12px 32px;
    }
`;

export const PageHeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    @media screen and (max-width: 480px) {
        flex-wrap: wrap;
        align-items: stretch;
    }
`;

export const PageHeader = styled.header`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    gap: 6px;
`;

export const PageEyebrow = styled.span`
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(131, 83, 226, 0.1);
    color: #6d41d8;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
`;

export const PageTitle = styled.h1`
    margin: 0;
    color: #111827;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.01em;
`;

export const PageSubtitle = styled.p`
    margin: 0;
    color: #64748b;
    font-size: 14px;
    line-height: 1.5;
`;

export const Layout = styled.div`
    display: grid;
    grid-template-columns: 300px minmax(0, 1fr);
    gap: 20px;
    align-items: start;

    @media screen and (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

export const Sidebar = styled.aside`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    border-radius: 18px;
    border: 1px solid rgba(148, 163, 184, 0.18);
    background:
        radial-gradient(circle at top right, rgba(131, 83, 226, 0.06), transparent 42%),
        linear-gradient(180deg, #ffffff 0%, #fcfbff 100%);
    box-shadow:
        0 18px 44px rgba(15, 23, 42, 0.05),
        0 1px 2px rgba(15, 23, 42, 0.04);
    /* Full height of content — no inner scroll; the window scrolls instead. */
    position: sticky;
    top: 16px;
`;

export const SidebarEmpty = styled.div`
    padding: 14px 16px;
    border-radius: 14px;
    background: rgba(148, 163, 184, 0.08);
    color: #64748b;
    font-size: 13px;
    line-height: 1.5;
    text-align: center;
`;

export const CountryGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const CountryHeader = styled.button<{ expanded: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.82);
    color: #111827;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition:
        transform 0.2s ease,
        border-color 0.2s ease,
        box-shadow 0.2s ease,
        background 0.2s ease;

    &:hover {
        transform: translateY(-1px);
        border-color: rgba(131, 83, 226, 0.32);
        box-shadow: 0 10px 20px rgba(15, 23, 42, 0.06);
    }

    img {
        width: 28px;
        height: 20px;
        border-radius: 4px;
        object-fit: cover;
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
    }
`;

export const CountryName = styled.span`
    flex: 1 1 auto;
    min-width: 0;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const CountryChevron = styled.span<{ expanded: boolean }>`
    display: inline-flex;
    align-items: center;
    color: #6d41d8;
    transform: rotate(${({ expanded }) => (expanded ? '180deg' : '0deg')});
    transition: transform 0.2s ease;

    svg {
        width: 14px;
        height: 14px;
    }
`;

export const ProgramList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-left: 4px;
`;

export const ProgramItem = styled.button<{ active: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 12px;
    border-radius: 14px;
    border: 1px solid
        ${({ active }) => (active ? 'rgba(131, 83, 226, 0.35)' : 'rgba(148, 163, 184, 0.18)')};
    background: ${({ active }) =>
        active
            ? 'linear-gradient(135deg, rgba(131, 83, 226, 0.12) 0%, rgba(131, 83, 226, 0.04) 100%)'
            : 'rgba(255, 255, 255, 0.82)'};
    box-shadow: ${({ active }) =>
        active ? '0 12px 26px -18px rgba(131, 83, 226, 0.5)' : '0 1px 2px rgba(15, 23, 42, 0.04)'};
    cursor: pointer;
    transition:
        transform 0.2s ease,
        background 0.2s ease,
        border-color 0.2s ease,
        box-shadow 0.2s ease;
    text-align: left;
    font-family: inherit;

    &:hover {
        transform: translateY(-1px);
        border-color: rgba(131, 83, 226, 0.32);
        box-shadow: 0 10px 22px rgba(15, 23, 42, 0.06);
    }
`;

export const ProgramType = styled.span`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #6b7280;
`;

export const ProgramItemBody = styled.span`
    display: flex;
    align-items: center;
    gap: 10px;
`;

export const ProgramName = styled.span`
    flex: 1 1 auto;
    min-width: 0;
    color: #111827;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    word-break: break-word;
`;

export const ProgramScorePill = styled.span<{ color: string }>`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 48px;
    padding: 3px 10px;
    border-radius: 999px;
    background: ${({ color }) => color};
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
`;

export const BodyCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 24px;
    border-radius: 18px;
    border: 1px solid rgba(148, 163, 184, 0.18);
    background: #ffffff;
    box-shadow:
        0 18px 44px rgba(15, 23, 42, 0.05),
        0 1px 2px rgba(15, 23, 42, 0.04);

    @media screen and (max-width: 768px) {
        padding: 18px 16px;
    }
`;

export const BodyHeader = styled.header`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-bottom: 14px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.18);
`;

export const BodyEyebrow = styled.span`
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(131, 83, 226, 0.1);
    color: #6d41d8;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
`;

export const BodyTitle = styled.h2`
    margin: 0;
    color: #111827;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: -0.01em;
`;

export const BodySubtitle = styled.p`
    margin: 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.5;
`;

export const MetaGrid = styled.dl`
    margin: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
`;

export const MetaItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 12px;
    border-radius: 12px;
    background: rgba(148, 163, 184, 0.08);
`;

export const MetaLabel = styled.dt`
    margin: 0;
    color: #6b7280;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
`;

export const MetaValue = styled.dd`
    margin: 0;
    color: #111827;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    word-break: break-word;
`;

export const EmptyBody = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 40px 20px;
    border: 1px dashed rgba(148, 163, 184, 0.32);
    border-radius: 16px;
    background:
        radial-gradient(circle at top, rgba(131, 83, 226, 0.06), transparent 55%),
        rgba(255, 255, 255, 0.82);
    text-align: center;
`;

export const EmptyBodyIcon = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 999px;
    background: linear-gradient(135deg, rgba(131, 83, 226, 0.18) 0%, rgba(131, 83, 226, 0.05) 100%);
    color: #6d41d8;

    svg {
        width: 26px;
        height: 26px;
    }
`;

export const EmptyBodyTitle = styled.h3`
    margin: 0;
    color: #111827;
    font-size: 16px;
    font-weight: 700;
`;

export const EmptyBodyText = styled.p`
    margin: 0;
    max-width: 360px;
    color: #64748b;
    font-size: 13px;
    line-height: 1.5;
`;

export const AdviceRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

/* --- Backwards-compatible exports (kept for any lingering references) --- */

export const CrossSimpleIcon = styled(CrossSimpleIconComponent)`
    fill: #b91c1c;
`;

export const Badge = styled.div<{ passed: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 999px;
    background: ${({ passed }) => (passed ? 'rgba(34, 197, 94, 0.14)' : 'rgba(239, 68, 68, 0.14)')};
    color: ${({ passed }) => (passed ? '#15803d' : '#b91c1c')};
    border: 1px solid
        ${({ passed }) => (passed ? 'rgba(34, 197, 94, 0.28)' : 'rgba(239, 68, 68, 0.28)')};
    font-size: 12px;
    font-weight: 700;
`;
